import React from 'react';
import { Table, Input, Button, Dropdown, Menu, Checkbox, Steps, Space, Flex, Tag, TableColumnProps, Modal, message, Select } from 'antd';
import { DownOutlined } from '@ant-design/icons';
import ReusableTable from '../../../../components/TableComponent';
import { MasterConfigs } from '../../../../interfaces/entity';
import { constants } from '../../../../constants/common';
import ProfileCard from '../../../../components/ContactInfo/ProfileCard';
import moment from 'moment';
import StatusBadge from '../../../Leaves/AttendanceComponents/history/StatusBadge';
import SmallProfileCard from '../../../../components/ContactInfo/SmallProfileCard';
import { align } from 'suneditor/src/plugins';
import { useSelector, useDispatch } from 'react-redux';
import { postData } from '../../../../actions/developer';

const { Search } = Input;

const columns = [
    {
        title: '',
        dataIndex: 'checkbox',
        render: () => <Checkbox />,
        width: 50,
    },
    {
        title: 'EMPLOYEE',
        dataIndex: 'employee',
    },
    {
        title: 'DATE',
        dataIndex: 'date',
    },
    {
        title: 'TOTAL DAYS',
        dataIndex: 'totalDays',
    },
    {
        title: 'LEAVE TYPE',
        dataIndex: 'leaveType',
    },
    {
        title: 'STATUS',
        dataIndex: 'status',
    },
    {
        title: 'APPROVER',
        dataIndex: 'approver',
    },
];

const SalariesHoldArrears = () => {
    const [currentStep, setCurrentStep] = React.useState(0);
    const [actionModalVisible, setActionModalVisible] = React.useState(false);
    const [selectedRecord, setSelectedRecord] = React.useState<any>(null);
    const [selectedAction, setSelectedAction] = React.useState<string>('');
    const [actionReason, setActionReason] = React.useState('');
    const [loading, setLoading] = React.useState(false);
    const [refreshKey, setRefreshKey] = React.useState(0);
    const params = useSelector(({ app }) => app?.params);
    const currentMonth = moment(params?.selectedMonth || moment().format('MMM YYYY'), 'MMM YYYY').format('MM-YYYY');
    const dispatch = useDispatch();


    const getHoldType = () => {
        if (currentStep === 0) return 'salaryProcessingHold';
        if (currentStep === 1) return 'salaryPayoutHold';
        if (currentStep === 2) return 'voidSalaryProcessing';
        return null;
    };

    const handlePayAction = (record: any) => {
        setSelectedRecord(record);
        setActionModalVisible(true);
    };

    const handleConfirmAction = async () => {
        if (!selectedAction) {
            message.error('Please select an action');
            return;
        }

        setLoading(true);
        try {
            const response = await dispatch(postData('/run-payroll/update-salary-action', {
                salaryProcessingId: selectedRecord._id,
                action: selectedAction,
                reason: actionReason
            }));

            if (response?.code === 200) {
                message.success('Salary action updated successfully');
                setActionModalVisible(false);
                setSelectedAction('');
                setActionReason('');
                setSelectedRecord(null);
                // Refresh table data
                setRefreshKey(prev => prev + 1);
                dispatch({
                    type: 'REFRESH_TABLE',
                    refreshTable: true,
                });
            } else {
                message.error(response?.message || 'Failed to update salary action');
            }
        } catch (error) {
            message.error('Error updating salary action');
        } finally {
            setLoading(false);
        }
    };

    const getActionValue = (record: any) => {
        if (record.voidSalaryProcessing) return 'void_salary_processing';
        if (record.salaryPayoutHold) return 'salary_payout_hold';
        if (record.salaryProcessingHold) return 'salary_processing_hold';
        return 'process_as_salary';
    };

    const getActionLabel = (value: string) => {
        const labels: Record<string, string> = {
            'process_as_salary': 'Process as Salary',
            'salary_processing_hold': 'Salary Processing Hold',
            'salary_payout_hold': 'Salary Payout on hold',
            'void_salary_processing': 'Void salary processing'
        };
        return labels[value] || 'Process as Salary';
    };

    const getStatusInfo = (record: any) => {
        if (record.voidSalaryProcessing) {
            return { label: 'Void Salary Processing', color: 'red' };
        }
        if (record.salaryPayoutHold) {
            return { label: 'Salary Payout on Hold', color: 'orange' };
        }
        if (record.salaryProcessingHold) {
            return { label: 'Salary Processing Hold', color: 'blue' };
        }
        return { label: 'Process as Salary', color: 'green' };
    };

    const salaryHoldColumns: TableColumnProps[] = [
        {
            title: 'Employee',
            dataIndex: 'employee',
            align: 'left',
            render: (employee) => employee ? (
                <SmallProfileCard
                    name={constants.GET_FULL_NAME(employee)}
                    avatar={employee?.avatar}
                    subTitle={employee?.employeeId}
                />
            ) : '-'
        },
        {
            title: 'Month',
            dataIndex: 'monthYear',
            align: 'center',
            render: (month) => moment(month, 'MM-YYYY').format('MMMM YYYY')
        },
        {
            title: 'Status',
            align: 'center',
            width: 200,
            render: (_, record) => {
                const statusInfo = getStatusInfo(record);
                return (
                    <Tag color={statusInfo.color} style={{ fontSize: '12px', padding: '4px 12px' }}>
                        {statusInfo.label}
                    </Tag>
                );
            }
        },
        {
            title: 'Net Pay',
            dataIndex: 'netPay',
            align: 'center',
            render: (amount) => `₹${amount?.toFixed(2) || '0.00'}`
        },
        {
            title: 'Hold Reason',
            dataIndex: 'holdReason',
            align: 'center',
            render: (reason) => reason || '-'
        },
        {
            title: 'Hold Date',
            dataIndex: 'holdDate',
            align: 'center',
            render: (date) => date ? moment(date).format('DD MMM YYYY') : '-'
        },
        {
            title: 'Pay Action',
            align: 'center',
            width: 250,
            render: (_, record) => {
                const currentValue = getActionValue(record);

                return (
                    <Select
                        value={currentValue}
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            setSelectedAction(value);
                            setSelectedRecord(record);
                            setActionModalVisible(true);
                        }}
                        options={[
                            { value: 'process_as_salary', label: 'Process as Salary' },
                            { value: 'salary_processing_hold', label: 'Salary Processing Hold' },
                            { value: 'salary_payout_hold', label: 'Salary Payout on hold' },
                            { value: 'void_salary_processing', label: 'Void salary processing' }
                        ]}
                    />
                );
            }
        }
    ];

    const salaryHoldConfig: MasterConfigs = {
        api: () => '/run-payroll/salary-holds',
        queryParams: {
            month: currentMonth,
            holdType: getHoldType()
        },
        tableProps: {
            rowKey: '_id',
            scroll: { y: 450 }
        },
        getColumns: () => salaryHoldColumns
    };

    const leaveConfig: MasterConfigs = {
        api: () => '/run-payroll/leaves',
        queryParams: {
            month: currentMonth
        },
        tableProps: {
            rowKey: '_id',
            rowSelection: {
                type: 'checkbox',
                onChange: (selectedRowKeys, selectedRows) => {
                    console.log('Selected Row Keys:', selectedRowKeys);
                    console.log('Selected Rows:', selectedRows);
                }
            },
        },
        getColumns: () => [
            {
                title: 'Employee',
                dataIndex: 'user',
                ellipsis: true,
                width: 150,
                render: (text, records) => <SmallProfileCard
                    name={constants.GET_FULL_NAME(text)}
                    avatar={text?.avatar}
                    subTitle={text.workEmail}
                />
            },
            {
                title: 'From Date',
                dataIndex: 'startDate',
                align: 'center',
                render: (text) => moment(text).format('DD MMM YYYY')
            },
            {
                title: 'Total Days',
                dataIndex: 'days',
                align: 'center',
                render: (text) => text + ' Days'
            },
            {
                title: 'Effective Leave Days in Month',
                dataIndex: 'effectiveLeaveDaysInMonth',
                render: (text) => text + ' Days',
                align: 'center',
            },
            {
                title: 'Leave Type',
                dataIndex: 'leaves',
                render: (text) => text.leaveType?.leaveName || 'N/A'
            },
            {
                title: 'Leave Status',
                align: 'center',
                dataIndex: 'leaveStatus',
                render: (text) => {
                    const statusMap = {
                        'approved': { color: 'green', text: 'Approved' },
                        'pending': { color: 'orange', text: 'Pending' }
                    };
                    const status = statusMap[text] || { color: 'blue', text: 'Unknown' };
                    return <Tag color={status?.color} bordered={false}>{status.text}</Tag>;
                }
            }
        ]
    }

    const config: MasterConfigs = {
        api: () => '/run-payroll/leave-and-no-attendance',
        queryParams: {
            month: '06-2025'
        },
        tableProps: {
            rowKey: '_id',
            // rowSelection: {
            //     type: 'checkbox',
            //     onChange: (selectedRowKeys, selectedRows) => {
            //         console.log('Selected Row Keys:', selectedRowKeys);
            //         console.log('Selected Rows:', selectedRows);
            //     }
            // },
            scroll: { y: 450 },
            expandable: {
                expandedRowRender: (record) => {
                    return <Table
                        className='w-full mt-2'
                        columns={getAttendanceColumns()}
                        dataSource={record.attendanceLogs.map((log) => ({
                            key: log._id,
                            ...log,
                        }))}
                        scroll={{ y: 300 }}
                        pagination={{
                            pageSize: 7
                        }}
                        rowClassName={(record) =>
                            record.dayStatus === "absent" || record.totalHours < 1 && record.dayStatus == 'present'
                                ? "bg-red-50"
                                : ""
                        }
                    />
                },
                rowExpandable: (record) => true,
            }
        },
        getColumns: () => employeeColumns as TableColumnProps[],
    }

    const getAttendanceColumns = (): TableColumnProps[] => [
        {
            title: "Date",
            dataIndex: "date",
            width: 200,
            fixed: 'left',
            render: (date) => moment(date).format("dddd DD MMM, YYYY"),
        },
        {
            title: 'Clock In',
            dataIndex: 'startDate',
            key: 'startDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchStatus == 'on-time') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.startDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + (record?.punchStatus == 'on-time' ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchStatus}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: 'Clock Out',
            dataIndex: 'endDate',
            key: 'endDate',
            align: 'center',
            width: 150,
            onCell: (record) => {
                let backgroundColor = '';

                if (record.punchOut == 'on-time' || record.punchOut == 'late') backgroundColor = '#d9f7be'; // red-ish
                else backgroundColor = '#ffccc7'; // green-ish

                if (!record?.endDate) {
                    backgroundColor = ''
                }

                return {
                    style: {
                        backgroundColor,
                    },
                };
            },
            render: (text: any, record: any) => {
                return text ?
                    <Flex justify="space-between">
                        {moment(text).format('hh:mm A')}
                        <span className={'first-letter:uppercase pl-2 -mt-1 font-semibold ' + ((record?.punchOut == 'on-time' || record?.punchOut == 'late') ? 'text-green-700' : 'text-red-600')} >
                            {record?.punchOut}
                        </span>
                    </Flex>
                    : '-'
            }
        },
        {
            title: "Working Hours",
            dataIndex: "totalHours",
            width: 150,
            align: 'center',
            render: (hours, record) => record?.dayStatus == 'present' ? <>
                {
                    hours <= 5 ? (
                        <span className='text-red-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) : hours > 5 && hours <= 7 ? (
                        <span className='text-red-800 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                    ) :
                        <span className='text-green-600 font-semibold'>{`${hours.toFixed(2)} hrs`}</span>
                }
            </> : '-',
        },
        {
            title: "Day Status",
            dataIndex: "dayStatus",
            width: 150,
            align: 'center',
            render: (status) => {
                const colorMap: Record<string, string> = {
                    present: "green",
                    absent: "red",
                    weekend: "blue",
                    leave: "orange",
                };
                return <Tag bordered={false} color={colorMap[status] || "default"}>{status.toUpperCase()}</Tag>;
            },
        },
        {
            title: "Leave Type",
            width: 150,
            render: (_, record) =>
                record.leaveData?.leaveDetail?.leaveTypeDetail?.leaveName || "-",
        },
        {
            title: "Leave Status",
            width: 150,
            align: 'center',
            render: (_, record) =>
                record.leaveData?.leaveStatus ? (
                    <Tag color="green">{record.leaveData.leaveStatus}</Tag>
                ) : (
                    "-"
                ),
        },
    ];

    const employeeColumns = [
        {
            title: "Employee",
            align: 'left',
            render: (_, record) => <SmallProfileCard
                name={constants.GET_FULL_NAME(record)}
                avatar={record?.avatar}
                subTitle={record?.employeeId}
            />
        },
        {
            title: "Shift",
            align: 'center',
            dataIndex: "shiftName",
        },
        {
            title: "Working Days",
            align: 'center',
            dataIndex: "workingDays",
        },
        {
            title: "Present Days",
            align: 'center',
            dataIndex: "presentDays",
        },
        {
            title: "Absent Days",
            align: 'center',
            dataIndex: "absentDays",
        },
        {
            title: "Total Leaves",
            align: 'center',
            dataIndex: "totalLeaves",
        },
        {
            title: "Unpaid Leaves",
            align: 'center',
            dataIndex: "unpaidLeaves",
        },
    ];

    const configs = {
        0: salaryHoldConfig,
        1: salaryHoldConfig,
        2: salaryHoldConfig,
    }


    return (
        <div style={{ display: 'flex', flexDirection: 'column', maxHeight: '70vh' }}>
            <div style={{ flex: '0 0 auto' }} className='bg-white shadow-md p-4  w-full'>
                <Flex align='center' gap={16}>
                    <div className='w-3/4 flex items-center gap-4'>
                        <Steps
                            current={currentStep}
                            size='small'
                            onChange={(e) => setCurrentStep(e)}
                            items={[
                                { title: 'Salary Processing Hold' },
                                { title: 'Salary Payout on Hold' },
                                { title: 'Void Salary Processing' },
                            ]}
                        />
                    </div>
                    <div className='w-1/4 flex justify-end'>
                        <Button type="primary">Save & Continue</Button>
                    </div>
                </Flex>
            </div>

            <div
                style={{
                    flex: '1 1 auto',
                    overflowY: 'auto',
                    padding: '1.5rem',
                    backgroundColor: '#f9fafb' // same as bg-gray-50
                }}
            >
                {currentStep === 0 && (
                    <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                        All salaries marked as "Salary Processing Hold" for this payroll cycle month will be displayed here. This includes salaries held from New Joinees section.
                    </div>
                )}
                {currentStep === 1 && (
                    <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                        All salaries on payout hold for this payroll cycle month will be displayed here.
                    </div>
                )}
                {currentStep === 2 && (
                    <div className="bg-purple-100 text-purple-800 p-4 rounded mb-4 text-sm font-medium">
                        All voided salary processing records for this payroll cycle month will be displayed here.
                    </div>
                )}
                {currentStep >= 0 && <ReusableTable config={configs[currentStep]} key={`${currentStep}-${refreshKey}`} />}
            </div>

            <Modal
                title="Update Pay Action"
                open={actionModalVisible}
                onOk={handleConfirmAction}
                onCancel={() => {
                    setActionModalVisible(false);
                    setSelectedAction('');
                    setActionReason('');
                    setSelectedRecord(null);
                }}
                confirmLoading={loading}
            >
                <div style={{ marginBottom: '16px' }}>
                    <strong>Employee:</strong> {selectedRecord?.employee && constants.GET_FULL_NAME(selectedRecord.employee)}
                </div>
                <div style={{ marginBottom: '16px' }}>
                    <strong>Selected Action:</strong> {selectedAction.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </div>
                <div>
                    <strong>Reason (Optional):</strong>
                    <Input.TextArea
                        rows={4}
                        value={actionReason}
                        onChange={(e) => setActionReason(e.target.value)}
                        placeholder="Enter reason for this action..."
                        style={{ marginTop: '8px' }}
                    />
                </div>
            </Modal>
        </div>

    );
};

export default SalariesHoldArrears;
